/**
 * "See as a typical user" (Sasha 10/14): an admin flips Central (the home
 * panel, Spotlight, This or That, the Central pages) to what someone with no
 * Central role sees, and back. The banner stays up the whole time so nobody
 * forgets they are in it. State and the `?as=user` reads live in api.ts.
 */

import { Button, makeStyles, mergeClasses } from '@fluentui/react-components';
import { EyeRegular, PersonRegular } from '@fluentui/react-icons';
import { borderRadius, veracityColors } from '@shared/ui';
import { setViewAsUser, useCentralSummary } from './api';

const useStyles = makeStyles({
  banner: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flexWrap: 'wrap',
    padding: '10px 14px',
    borderRadius: borderRadius.large,
    backgroundColor: veracityColors.midnightCharcoal,
    color: '#FFFFFF',
    fontSize: '13.5px',
    lineHeight: 1.45,
  },
  icon: { fontSize: '20px', flexShrink: 0 },
  text: { flex: 1, minWidth: '220px' },
  strong: { fontWeight: 700 },
  toggle: { flexShrink: 0 },
});

/** The always-on reminder while seeing as a typical user, with the way back. */
export function ViewAsUserBanner({ className }: { className?: string }) {
  const styles = useStyles();
  const c = useCentralSummary().data;
  if (!c?.asUser) return null;
  return (
    <div className={mergeClasses(styles.banner, className)} role="status">
      <PersonRegular className={styles.icon} aria-hidden />
      <span className={styles.text}>
        <span className={styles.strong}>You are seeing Central as a typical user.</span> No admin buttons, and poll results only
        where everyone can see them. Votes, hearts and comments you make here are still real.
      </span>
      <Button className={styles.toggle} size="small" appearance="primary" onClick={() => setViewAsUser(false)}>
        Back to admin view
      </Button>
    </div>
  );
}

/** The switch into the typical user's view; renders only for a Central admin not already in it. */
export function ViewAsUserButton({ size = 'small', appearance = 'subtle' }: { size?: 'small' | 'medium'; appearance?: 'subtle' | 'secondary' | 'transparent' }) {
  const c = useCentralSummary().data;
  if (!c?.realAdmin || c.asUser) return null;
  return (
    <Button size={size} appearance={appearance} icon={<EyeRegular />} onClick={() => setViewAsUser(true)}>
      See as a typical user
    </Button>
  );
}
